import type { QuestionEffort } from "@ui/organisms/QuestionBox/questionEfforts";
import type { ChatPanelController } from "~/modules/shared/chat/chat-panel/ChatPanel";
import type { CollectedMentions } from "~/modules/shared/chat/chat-panel/mentionCollector";
import {
	hideVideoEvidence,
	scrollVideoEvidenceOffView,
	showVideoEvidence,
} from "~/modules/shared/chat/chat-panel/videoEvidence";
import {
	appEffort,
	dispatchAskFollowUpResult,
	type MobileAskResult,
	type MobileThreadView,
} from "~/modules/shared/chat/mobileChatInterface";
import type { AskedQuestion } from "./components/useAskPageForm";
import { postAskPageQuestion } from "./postAskPageQuestion";

type Options = {
	handle: string;
	/** The question a follow-up is asked from, read at the moment of asking. */
	threadRootId: () => string;
	/** The effort to ask at when the app names none. */
	effort: () => QuestionEffort | undefined;
	/** A reason to refuse without asking, or undefined to go ahead. */
	refusal: () => string | undefined;
	/** Shows the created follow-up in place. */
	onAsked: (asked: AskedQuestion) => void;
	/**
	 * Events the app registered for when it opened the page, which the first
	 * answer starts streaming before the app could register for them itself.
	 */
	registeredEvents?: string[];
};

/**
 * The `window.view` of a public AskPage thread, or of a shared snapshot of one.
 * The thread is one conversation with one controller, and a follow-up is
 * another interaction of it.
 *
 * The app has its own composer and presents any refusal natively, so asking
 * shows nothing on the page itself and only reports the outcome back.
 */
export class AskPageThreadMobileInterface implements MobileThreadView {
	private controller: ChatPanelController | undefined;
	private registeredEvents: string[];
	private readonly options: Options;
	private asking = false;

	constructor(options: Options) {
		this.options = options;
		this.registeredEvents = options.registeredEvents ?? [];
	}

	/** The conversation gets whatever the app has registered for, from its first event. */
	setController = (controller: ChatPanelController) => {
		this.controller = controller;
		controller.registerForChatEvents(this.registeredEvents);
	};

	async askFollowUp(
		question: string,
		effort?: string | null,
	): Promise<MobileAskResult> {
		const chosen = appEffort(effort);
		if (chosen.ok === false) {
			return chosen;
		}
		const reason = this.asking ? "answer_in_progress" : this.options.refusal();
		if (reason !== undefined) {
			return { ok: false, reason };
		}
		this.asking = true;
		const result = await postAskPageQuestion({
			handle: this.options.handle,
			prompt: question,
			threadRootId: this.options.threadRootId(),
			effort: chosen.effort ?? this.options.effort(),
		});
		this.asking = false;
		if (result.ok === true) {
			this.options.onAsked({ id: result.id, path: result.path, question });
		}
		return result;
	}

	askFollowUpWithCallback(
		requestId: string,
		question: string,
		effort?: string | null,
	) {
		dispatchAskFollowUpResult(requestId, this.askFollowUp(question, effort));
	}

	ask(question: string, effort?: string | null) {
		return this.askFollowUp(question, effort);
	}

	registerForChatEvents(events: string[]) {
		this.registeredEvents = events;
		this.controller?.registerForChatEvents(events);
	}

	scrollVideoEvidenceOffView(transactionID: string, offset: number) {
		return scrollVideoEvidenceOffView(transactionID, offset);
	}

	hideVideoEvidence(transactionID: string) {
		hideVideoEvidence(transactionID);
	}

	showVideoEvidence(transactionID: string) {
		showVideoEvidence(transactionID);
	}

	getMentions(): CollectedMentions {
		return (
			this.controller?.getMentions() ?? {
				social_account_mention: [],
				influencer_mention: [],
				video_mention: [],
			}
		);
	}
}
